/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	Input,
	OnDestroy,
	OnChanges,
	ChangeDetectorRef,
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormBuilder,
	FormControl,
	UntypedFormGroup,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import * as appStrings from '@touchplan/strings';
import { ProjectMember, Role } from '@touchplan/common';
import { of, Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { sortArray } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { sortRolesByName } from 'src/app/utils/array-util';
import { MatDialog } from '@angular/material/dialog';
import { InvitedUser } from 'src/app/app-data/models/InvitedUser';
import { AbstractAddEditItemComponent } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { Participant } from './project-member-dialog.component';
import { Subject } from 'rxjs';
import { emailFormatValidator } from 'src/app/common/form-utils/validators';

interface IParticipant extends ProjectMember {
	user: InvitedUser;
}

/**
 * Angular validator that returns an error if the item is in the list
 * @param list list of items to validate against
 * @returns  returns an error if the item is in the list, otherwise null
 */
function listValidator(list: string[]): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const controlValue =
			control?.value && typeof control.value === 'string' ? control.value.toLocaleLowerCase() : null;
		return controlValue && list.length && list.map((item) => item.toLocaleLowerCase()).includes(controlValue)
			? { itemInList: 'error' }
			: null;
	};
}

/**
 * Future note:
 * This code operates with a formControl that can either have a value which is a string, or a value which is an array of strings.
 * It will have an array of string while the isAdmin checkbox is falsy, and a string while it's truthy
 */
@Component({
	selector: 'add-edit-member',
	templateUrl: './add-edit-member.component.html',
	styleUrls: ['./add-edit-member.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEditMemberComponent
	extends AbstractAddEditItemComponent<Participant>
	implements OnInit, OnDestroy, OnChanges
{
	@Input() public rolesApi: RolesApi;
	@Input() public isOwner: boolean;
	@Input() public itemList$: Observable<Array<IParticipant | InvitedUser>>;
	@Input() public itemInEdit = false;
	public appStrings = appStrings;

	public strings = appStrings;
	public sortedRoles$: Observable<Role[]>; // sorted roles
	public isInvitedUser: boolean;

	/** workaround to clean up an is admin subscription */
	private pmChange$ = new Subject<void>();

	constructor(
		protected dialog: MatDialog,
		protected builder: UntypedFormBuilder,
		private cd: ChangeDetectorRef
	) {
		super(dialog);
	}

	public ngOnInit() {
		super.ngOnInit();

		// sort roles alphabetically by name
		this.sortedRoles$ = this.rolesApi.data.list$.pipe(
			map((roleMap) => Array.from(roleMap.values())),
			sortArray(of(sortRolesByName)),
			takeUntil(this.shutdownSubject$)
		);

		this.isInvitedUser = this.item && this.item instanceof InvitedUser;
		this.itemList$
			?.pipe(
				map((items) => {
					if (items?.length) {
						return items.map((item) => {
							return item instanceof ProjectMember ? item.view?.user?.email : item.view?.email;
						});
					}
					return null;
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((items) => {
				if (items) {
					this.itemFormGroup
						.get('email')
						?.setValidators([Validators.required, emailFormatValidator(), listValidator(items)]);
					this.itemFormGroup.get('email')?.updateValueAndValidity({ emitEvent: false });
				}
			});
	}

	public ngOnDestroy() {
		super.ngOnDestroy();
	}

	public ngOnChanges(changes) {
		super.ngOnChanges(changes);
	}

	protected createFormGroup(member: Participant): UntypedFormGroup {
		if (member) {
			return this.builder.group({
				roleId: [null, Validators.required],
				roles: [null],
				admin: [false],
			});
		} else {
			return this.builder.group({
				email: ['', Validators.compose([Validators.required, emailFormatValidator()])],
				roleId: [null, Validators.required],
				roles: [null],
				admin: [false],
			});
		}
	}

	protected updateFormGroup(item: Participant) {
		this.pmChange$.next();

		// editing existing project member or invited user
		const secondary = item.view.secondaryRoles;
		const roleId = item.view.roleId;
		const roles = secondary ? Object.getOwnPropertyNames(secondary) : [];

		this.itemFormGroup.patchValue({ roles, roleId, admin: item.view.accessLevel === 'Admin' });
		this.cd.detectChanges();

		// disable if owner
		if (this.isOwner) {
			this.itemFormGroup.get('admin').disable();
		} else {
			this.itemFormGroup.get('admin').enable();
		}

		this.isInvitedUser = item instanceof InvitedUser;
	}

	public doResend($event) {
		this.autoHideLabel.show();

		this.handleItemEvent({
			type: 'RESEND',
			event: $event,
		});
	}

	public needMultiSelect(form: UntypedFormGroup): boolean {
		return !form.get('admin').value;
	}
}
